import { ActiveProjectStage, LeadStage, PortfolioStage, ProjectStage } from '@client/features/project/types';
import { currentUserHasFeatureAccess } from '@client/features/software-feature/utils/current-user-has-feature-access';
import { SoftwareFeature } from '@client/features/software-feature/types';
import { getAppConfig } from '@client/core/config/get-app-config';

// Map of Project Stage constants to human-readable text for the Workspace
const stageMap = getAppConfig().config.project.stages;

/**
 * Human-readable Project Stage. Such as which phase of the renovation lifecycle
 * the Project is in.
 */
export function projectStageText(stage: ProjectStage): string {
  return stageMap[stage];
}

/**
 * Human-readable Project Stage Group. Each Project Stage is within a Group.
 * TODO: The text labels like "Lead" and "Active Project" should come from getAppConfig().config.project.stageOptions instead of being hardcoded
 */
export function projectStageGroupText(stage: ProjectStage): string {
  if (isStageInLeadStage(stage)) {
    return 'Lead';
  }

  if (isStageInActiveProjectStage(stage)) {
    return 'Active Project';
  }

  if (isStageInPortfolioStage(stage)) {
    return 'Portfolio';
  }

  return 'Unknown';
}

export interface PipelineStageRadioOption {
  label: string;
  value: ProjectStage;
}

/**
 * Maps a human-readable string like "Active Project" Pipeline group with an
 * array of its data for creating a radio button option for each Stage within
 * that group.
 */
export function getPipelineStageOptionsByGroup(): Record<string, PipelineStageRadioOption[]> {
  const options = {};

  const stageToOption = (stage: ProjectStage): PipelineStageRadioOption => ({
    label: projectStageText(stage),
    value: stage,
  });

  if (currentUserHasFeatureAccess(SoftwareFeature.LeadsDashboard)) {
    options['Lead Pipeline'] = LEAD_STAGES.map(stageToOption);
  }

  if (currentUserHasFeatureAccess(SoftwareFeature.ActiveProjectsDashboard)) {
    options['Active Project Pipeline'] = ACTIVE_PROJECT_STAGES.map(stageToOption);
  }

  if (currentUserHasFeatureAccess(SoftwareFeature.PortfolioDashboard)) {
    options['Portfolio'] = PORTFOLIO_STAGES.map(stageToOption);
  }

  return options;
}

/**
 * Check whether the given ProjectStage is within the group of stages called
 * "Lead"
 */
const LEAD_STAGES: LeadStage[] = [
  ProjectStage.Lead,
  ProjectStage.ContactingSeller,
  ProjectStage.AppointmentSet,
  ProjectStage.OfferMade,
  ProjectStage.Negotiation,
  ProjectStage.PendingPurchase,
];
export function isStageInLeadStage(stage: ProjectStage): boolean {
  return LEAD_STAGES.includes(stage as LeadStage);
}

/**
 * Check whether the given ProjectStage is within the group of stages called
 * "Active Project"
 */
const ACTIVE_PROJECT_STAGES: ActiveProjectStage[] = [
  ProjectStage.ConstructionOnHold,
  ProjectStage.PlanningPermitting,
  ProjectStage.UnderConstruction,
  ProjectStage.PunchList,
  ProjectStage.ActiveListing,
  ProjectStage.PendingSale,
];
export function isStageInActiveProjectStage(stage: ProjectStage): boolean {
  return ACTIVE_PROJECT_STAGES.includes(stage as ActiveProjectStage);
}

/**
 * Check whether the given ProjectStage is within the group of stages called
 * "Portfolio"
 */
const PORTFOLIO_STAGES: PortfolioStage[] = [
  ProjectStage.CompletedPortfolio,
  ProjectStage.RentalInventory,
  ProjectStage.WholesaleInventory,
];
export function isStageInPortfolioStage(stage: ProjectStage): boolean {
  return PORTFOLIO_STAGES.includes(stage as PortfolioStage);
}
